import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Helmet } from 'react-helmet-async'
import Typography from 'src/components/atoms/Typography'
import { useChatStore } from 'src/enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { ROUTES } from '../../types/routes'
import { api } from '../../api/root'
import FeltMapView from './FeltMapView'
import ProjectEmbedView from './ProjectEmbedView'
import { LayerUploadStatus } from '../../types/layer'
import { ProjectStatusLayer } from '../../types/projects'
import cometLoader from 'src/assets/comet-loader.svg'
import { useProjectTenantFallback } from 'src/enviroment/hooks/useProjectTenantFallback'

const TERMINAL_STATUSES = [LayerUploadStatus.COMPLETED, LayerUploadStatus.ERROR]

// A removed and re-added layer keeps its layerId but gets a new feltLayerId, so only the latter shows the re-add.
const getCompletedLayersSignature = (layers: ProjectStatusLayer[] = []): string =>
  layers
    .filter((l) => l.status === LayerUploadStatus.COMPLETED)
    .map((l) => l.feltLayerId ?? l.layerId)
    .sort()
    .join(',')

const ProjectDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams() as { id: string }
  const { setSelectedCountry, upsertMessageByType, setIsExpanded, clearMessages } = useChatStore(
    useShallow((s) => ({
      setSelectedCountry: s.setSelectedCountry,
      upsertMessageByType: s.upsertMessageByType,
      setIsExpanded: s.setIsExpanded,
      clearMessages: s.clearMessages,
    })),
  )

  const { data, isFetching, isError } = api.project.byId.useQuery(id)
  const { isSwitchingTenant } = useProjectTenantFallback(id, { hasProject: Boolean(data), isFetching })

  // Poll /status while layers are still being processed by Felt.
  // As layers become COMPLETED or get removed, remount the Felt embed (debounced to batch layers that come at the same time).
  const { data: statusData } = api.project.status.useQuery(id, Boolean(data?.id) && !data?.embedUrl)

  const aoiLayerReady = statusData?.aoiLayerReady ?? false
  const totalCount = statusData?.layers.length ?? 0
  const completedSignature = getCompletedLayersSignature(statusData?.layers)
  const settledCount = (statusData?.layers ?? []).filter(
    (l) =>
      l.status === LayerUploadStatus.COMPLETED ||
      l.status === LayerUploadStatus.ERROR ||
      l.status === LayerUploadStatus.NO_DATA,
  ).length
  const statusLayerUpload = statusData?.layerUploadStatus ?? data?.layerUploadStatus
  const statusCopaSettled = !statusData || TERMINAL_STATUSES.includes(statusData.copaStatus)
  const projectAlreadyReady = statusLayerUpload && TERMINAL_STATUSES.includes(statusLayerUpload) && statusCopaSettled
  const allLayersSettled = Boolean(projectAlreadyReady) || totalCount === 0 || settledCount === totalCount

  const REMOUNT_DEBOUNCE_MS = 1500

  const [remountKey, setRemountKey] = useState(0)
  const [snapshotLayers, setSnapshotLayers] = useState(statusData?.layers ?? [])
  const [snapshotAoiLayerReady, setSnapshotAoiLayerReady] = useState(aoiLayerReady)
  const lastSeenSignatureRef = useRef<string | null>(null)
  const aoiRemountedRef = useRef(false)
  const pendingRemountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerRemount = useCallback(() => {
    // Snapshot before bumping the key so the new FeltMapView instance receives
    // accurate legend data on its very first render, with no effect-lag flash.
    setSnapshotLayers(statusData?.layers ?? [])
    setSnapshotAoiLayerReady(statusData?.aoiLayerReady ?? false)
    setRemountKey((k) => k + 1)
  }, [statusData])

  // Remount once as soon as the AOI layer is ready, so the user sees the project
  // boundary immediately — before any worker layers finish processing.
  useEffect(() => {
    if (aoiLayerReady && !aoiRemountedRef.current) {
      aoiRemountedRef.current = true
      triggerRemount()
    }
  }, [aoiLayerReady, triggerRemount])

  useEffect(() => {
    if (!statusData) return

    if (lastSeenSignatureRef.current === null) {
      lastSeenSignatureRef.current = completedSignature
      setSnapshotLayers(statusData.layers)
      return
    }

    if (completedSignature !== lastSeenSignatureRef.current) {
      const isFinalBatch = allLayersSettled
      lastSeenSignatureRef.current = completedSignature

      if (pendingRemountTimerRef.current) {
        clearTimeout(pendingRemountTimerRef.current)
        pendingRemountTimerRef.current = null
      }

      if (isFinalBatch) {
        triggerRemount()
      } else {
        pendingRemountTimerRef.current = setTimeout(() => {
          triggerRemount()
          pendingRemountTimerRef.current = null
        }, REMOUNT_DEBOUNCE_MS)
      }
    }
  }, [completedSignature, statusData, allLayersSettled, triggerRemount])

  // USE FOR DEBUGGING LAYER/LEGEND CHANGES
  // useEffect(() => {
  //   if (!felt) return

  //   const unsubscribers: (() => void)[] = []

  //   const setupListeners = async () => {
  //     console.log('=== FELT INITIAL STATE ===')

  //     const layers = await felt.getLayers()

  //     const layerGroupIds = layers.map((layer) => layer?.groupId).filter(Boolean) as string[]
  //     const layerGroups = await felt.getLayerGroups({ ids: layerGroupIds })

  //     const legendItems = await felt.getLegendItems()

  //     legendItems.forEach((item) => {
  //       if (item) {
  //         console.log(`Legend Item "${item.title}":`, {
  //           id: item.id,
  //           layerId: item.layerId,
  //           visible: item.visible,
  //         })

  //         const unsubscribe = felt.onLegendItemChange({
  //           options: { layerId: item.layerId, id: item.id },
  //           handler: ({ legendItem }) => {
  //             console.log('=== LEGEND ITEM CHANGED ===', legendItem)
  //           },
  //         })
  //         unsubscribers.push(unsubscribe)
  //       }
  //     })

  //     // Set up listener for each layer
  //     layers.forEach((layer) => {
  //       if (layer) {
  //         console.log(`Layer "${layer.name}":`, {
  //           id: layer.id,
  //           groupId: layer.groupId,
  //           visible: layer.visible,
  //           style: layer.style,
  //         })

  //         // Listen for changes on this specific layer
  //         const unsubscribe = felt.onLayerChange({
  //           options: { id: layer.id },
  //           handler: ({ layer }) => {
  //             console.log('=== LAYER CHANGED ===', layer)
  //           },
  //         })
  //         unsubscribers.push(unsubscribe)
  //       }
  //     })

  //     // Set up listener for each layer group
  //     layerGroups.forEach((group) => {
  //       if (group) {
  //         console.log(`Group "${group.name}":`, {
  //           id: group.id,
  //           visible: group.visible,
  //         })

  //         const unsubscribe = felt.onLayerGroupChange({
  //           options: { id: group.id },
  //           handler: ({ layerGroup }) => {
  //             console.log('=== LAYER GROUP CHANGED ===', layerGroup)
  //           },
  //         })
  //         unsubscribers.push(unsubscribe)
  //       }
  //     })
  //   }

  //   setupListeners()

  //   return () => {
  //     unsubscribers.forEach((unsub) => unsub())
  //   }
  // }, [felt])

  useEffect(() => {
    return () => {
      if (pendingRemountTimerRef.current) {
        clearTimeout(pendingRemountTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (data) setSelectedCountry(data.location || '')
    return () => {
      setSelectedCountry('')
    }
  }, [data])

  useEffect(() => {
    if (location.pathname.includes('landscapes')) {
      // If the session contains only a single assistant greeting (no user messages),
      // clear it so the miombo report appears in a clean chat without the greeting.
      const { messages } = useChatStore.getState()
      const hasOnlyGreeting = messages.length === 1 && messages[0].role === 'assistant' && !messages[0].type
      if (hasOnlyGreeting) {
        clearMessages()
      }
      upsertMessageByType('miombo-report')
      setIsExpanded(true)
    }
  }, [location.pathname])

  if ((isFetching && !data) || isSwitchingTenant) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-center justify-center bg-gray-950">
          <img src={cometLoader} alt="Loading" width={56} height={56} className="animate-spin" />
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Typography variant="lg" className="text-red-500">
          Error loading project
        </Typography>
      </div>
    )
  }

  const handleGoBack = () => {
    if (location.pathname.includes('landscapes')) {
      navigate(ROUTES.LANDSCAPES)
    } else {
      navigate(ROUTES.PROJECTS)
    }
  }

  return (
    <>
      <Helmet>
        <title>RAMO - Project Details</title>
      </Helmet>

      <div className="flex h-full flex-col">
        {data.embedUrl ? (
          <ProjectEmbedView project={data} onBack={handleGoBack} stage={statusData?.stage} />
        ) : (
          <FeltMapView
            key={remountKey}
            project={data}
            projectId={id}
            onBack={handleGoBack}
            allLayersSettled={allLayersSettled}
            aoiLayerReady={snapshotAoiLayerReady}
            layers={projectAlreadyReady ? [] : snapshotLayers}
            legendLayers={projectAlreadyReady ? [] : statusData?.layers ?? snapshotLayers}
            copaStatus={statusData?.copaStatus}
            copaSummary={statusData?.copaSummary}
            stage={statusData?.stage}
          />
        )}
      </div>
    </>
  )
}

export default ProjectDetailsPage
